import React, { forwardRef, useState } from "react";
import { format, parseISO, getDaysInMonth, isWithinInterval, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getHolidays, getSchoolNameByClassroomId } from "@webapps/business";
import { useAuth } from "@webapps/reactjs-tech-components";
import { useQuery } from "react-query";
import AvatarCompany from "components/Avatar/AvatarCompany";
import { useCalendar } from "../Stores/CalendarProvider";
import planningColors, { planningColorsText } from "../../Classes/colors/planningColors";

const styles = {
  hiddenContent: {
    display: "none",
  },
  header: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    marginBottom: "5px",
  },
  left: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: "500px",
    // backgroundColor: "#333"
  },
  middle: {
    // width: "35%",
    width: "22%",
    // backgroundColor: "green"
    display: "flex",
    gap: "20px",
  },
  right: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "end",

    // backgroundColor: "blue",
    width: "23%",
    // width: "20%",
  },
  container: {
    marginLeft: "15px"
  },
  column: {
    display: "inline-block",
    verticalAlign: "top",
    width: "172px",
    margin: "2px",
    padding: "5px",
    backgroundColor: "#eee", // Fond global (pense à enlever si tu veux)
    border: "1px solid #ccc"
  },
  monthTitle: {
    textAlign: "center",
    margin: "0 0 10px 0",
    fontWeight: "bold",
    fontSize: "16px",
    textAlign: "center",
  },
  dayBox: {
    marginBottom: "4px", // espace entre lignes
    padding: "2px 6px",
    borderRadius: "4px",
  },
  footer: {

  },
  margin0: {
    margin: "0px",
    fontSize: "18px",
  },
  margin4: {
    margin: "4px",
  },
  pageBreak: {
    pageBreakAfter: "always",
  }
};


/**
 * Generates an array of Date objects representing all the days in the given month.
 *
 * @param {Date} monthDate - A Date object representing any date within the desired month.
 * @returns {Date[]} An array of Date objects for each day in the specified month.
 */
function getAllDaysOfMonth(monthDate) {
  const days = [];
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const totalDays = getDaysInMonth(monthDate);

  for (let day = 1; day <= totalDays; day++) {
    days.push(new Date(year, month, day));
  }

  return days;
}

/**
 * Découpe un tableau `array` en sous-tableaux de longueur `size`.
 * Ex: chunkArray([1,2,3,4,5], 2) => [[1,2],[3,4],[5]]
 */
function chunkArray(array, size) {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

/**
 * Retourne la période correspondant à un jour donné, ou undefined si aucune.
 */
function getPeriodForDay(day, periods) {
  return periods.find((p) => {
    const start = parseISO(p.startDate);
    const end = parseISO(p.endDate);
    // isWithinInterval de date-fns pour la lisibilité
    return isWithinInterval(day, { start, end });
  });
}

const PlanningToExport = forwardRef(({
  months, periods, holidays,
  classroomName, trainingName, trainingDegree,
  startDate, endDate,
  school, handlePdfReady,

}, ref) => {

  const [loadingPicture, setLoadingPicture] = useState(true);
  const { state, dispatch } = useCalendar()

  const handleFinishLoading = () => {
    setLoadingPicture(false);
  }

  const monthEntries = months.map((monthDate, index) => {
    const monthName = format(monthDate, 'MMMM', { locale: fr });
    const monthYear = format(monthDate, 'yyyy', { locale: fr });
    const addYear = monthName === "janvier";

    // All days in the month.
    const daysInMonth = getAllDaysOfMonth(monthDate);

    // Build the day boxes.
    const dayRows = daysInMonth.map((day, i) => {
      const period = getPeriodForDay(day, periods);
      const isWeekend = day.getDay() === 6 || day.getDay() === 0;
      const parsedStartDate = parseISO(startDate);
      const parsedEndDate = parseISO(endDate);
      const isOutsideRange = day < parsedStartDate || day > parsedEndDate;

      function isHolidayDay(day, holidays) {
        return holidays.some((holiday) => {
          const holidayDate = parseISO(holiday.date);
          return isSameDay(day, holidayDate);
        });
      }

      let backgroundColor = "#fff";
      let color = "black";

      if (isHolidayDay(day, holidays)) {
        backgroundColor = planningColors.FERIES;
        color = planningColorsText.FERIES;
      } else if (isOutsideRange) {
        backgroundColor = "#FAFAFA";
        color = "#D1D1D1";
      } else if (isWeekend) {
        backgroundColor = "#999999";
      } else if (period) {
        backgroundColor = planningColors[period.type] || "#fff";
        color = planningColorsText[period.type] || "black";
      }

      const dayLabel = format(day, 'EEEE dd', { locale: fr });

      return (
        <div
          key={i}
          style={{
            ...styles.dayBox,
            backgroundColor,
            color,
          }}
        >
          {dayLabel.charAt(0).toUpperCase() + dayLabel.slice(1)}
        </div>
      );
    });

    // Return an object containing all the data for this month.
    return { monthName, monthYear, addYear, dayRows, index };
  });

  const renderedColumns = [];
  monthEntries.forEach((entry) => {
    if (entry.addYear) {
      renderedColumns.push(
        <div key={`year-${entry.index}`} style={styles.column}>
          <h2 style={styles.monthTitle}>{entry.monthYear}</h2>
        </div>
      );
    }
    renderedColumns.push(
      <div key={`month-${entry.index}`} style={styles.column}>
        <h2 style={styles.monthTitle}>
          {entry.monthName.charAt(0).toUpperCase() + entry.monthName.slice(1)}
        </h2>
        {entry.dayRows}
      </div>
    );
  });

  const chunkedColumns = chunkArray(renderedColumns, 9);

  const Header = () => {
    return (
      <div style={styles.header}>
        <div style={styles.middle}>
          <div>
            <h2 style={styles.margin0}>CALENDRIER DE FORMATION</h2>
            <div>
              {(() => {
                let total = 0;
                return (
                  <>
                    {Object.entries(state.categoryTotals).map(([type, totals]) => {
                      if (type === "VACANCES") return null;
                      if (type === "FERIES") return null;
                      total += totals.hours;
                      return (
                        <p style={styles.margin4} key={type}>
                          {type}: {totals.days} jours ({totals.hours} heures)
                        </p>
                      );
                    })}
                    {/* Faire la somme pour avoir le totals */}
                    <p style={styles.margin4}>
                      Total {total} heures
                      {/* Total: {Object.values(categoryTotals).reduce((acc, { days }) => acc + days, 0)} jours */}
                    </p>
                  </>
                );
              })()}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
              <div style={
                {
                  backgroundColor: planningColors.FERIES,
                  color: planningColorsText.FERIES,
                  borderRadius: "100%",
                  height: "20px",
                  width: "20px",
                  border: "1px solid black",
                }
              }
              />
              <div style={styles.margin4}>Jours fériés</div>
              <div style={
                {
                  backgroundColor: "#999999",
                  borderRadius: "100%",
                  height: "20px",
                  width: "20px",
                  border: "1px solid black",
                }
              } />
              <div style={styles.margin4}>Week-end</div>
            </div>
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
              <div style={
                {
                  backgroundColor: planningColors.FORMATION,
                  color: planningColorsText.FORMATION,
                  borderRadius: "100%",
                  height: "20px",
                  width: "20px",
                  border: "1px solid black",
                }
              } />
              <div style={styles.margin4}>Formation</div>
              <div style={
                {
                  backgroundColor: planningColors.ENTREPRISE,
                  color: planningColorsText.ENTREPRISE,
                  borderRadius: "100%",
                  height: "20px",
                  width: "20px",
                  border: "1px solid black",
                }
              } />
              <div style={styles.margin4}>Entreprise</div>
            </div>
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
              <div style={
                {
                  backgroundColor: planningColors.VACANCES,
                  color: planningColorsText.VACANCES,
                  borderRadius: "100%",
                  height: "20px",
                  width: "20px",
                  border: "1px solid black",
                }
              } />
              <div style={styles.margin4}>Vacances</div>
            </div>
          </div>
        </div>
        <div style={styles.right}>
          <div style={{ marginRight: "16px" }}>
            <AvatarCompany idCompany={school?.schoolId}
              height={"100px"}
              width={"100px"}
              toHtml={true}
              onFinish={handlePdfReady}
            />
          </div>
          <div>
            <div>
              <p style={styles.margin4}>École : {school?.schoolName}</p>
              <p style={styles.margin4}>Classe : {classroomName}</p>
            </div>
            <p style={styles.margin4}>Formation (Diplôme) : {trainingName} ({trainingDegree})</p>
            {/* <p style={styles.margin4}>{trainingDegree}</p> */}
            <p style={styles.margin4}>Date : Du {format(parseISO(startDate), "dd/MM/yyyy")} au {format(parseISO(endDate), "dd/MM/yyyy")}</p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div ref={ref} style={styles.hiddenContent} className="hidden-content">
      <div style={styles.container}>
        {chunkedColumns.map((columnsGroup, groupIndex) => (
          <div key={groupIndex} style={styles.pageBreak}>
            <Header />
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              {columnsGroup}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

export default PlanningToExport;
